import React, { useState } from 'react';
import '../CSS/Careers.css';
import careerBanner from '../Images/career/Career-Banner.png';
import careerBannerMob from '../Images/Mobile Images/career/career-banner-mob.png';
import jobs, { Job } from '../TestData/jobs';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, TextField, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';

const Careers: React.FC = () => {
  const [selectedJobIdx, setSelectedJobIdx] = useState(0);
  const [expandedJobIdx, setExpandedJobIdx] = useState<number | null>(null);
  const jobsArray = Array.isArray(jobs) ? jobs : [];
  const hasValidJobs = jobsArray.length > 0;
  
  // Default empty job structure for when no jobs are available
  const emptyJob = {
    id: '',
    title: 'No positions available at this time',
    location: { state: '', country: '' },
    type: '',
    jobId: '',
    jobSchedule: '',
    payRange: '',
    jobFamily: '',
    shift: '',
    jdAbout: 'We are currently not accepting applications for any positions. Please check back later for new opportunities.',
    jdValues: [],
    jdMinQual: [],
    jdPrefQual: [],
    jdBenefits: []
  };
  
  const selectedJob = hasValidJobs ? jobsArray[selectedJobIdx] : emptyJob;

  const handleJobClick = (idx: number) => {
    setSelectedJobIdx(idx);
  };

  const handleMobileJobToggle = (idx: number) => {
    setExpandedJobIdx(expandedJobIdx === idx ? null : idx);
    setSelectedJobIdx(idx);
  };

  const renderJobDetails = (job: Job, includeForm: boolean = false) => (
    <>
      <div className="jd-details-title">{job.title}</div>
      <div className="jd-details-location">
        <LocationOnOutlinedIcon style={{fontSize: '1.3vw', color: '#757575', marginRight: '0.5vw', verticalAlign: 'middle'}} />
        {job.location.state && job.location.country 
          ? `${job.location.state}, ${job.location.country}` 
          : 'Location TBD'
        }
      </div>
      <div className="jd-details-quick-glance">
        <div className="jd-details-quick-glance-row jd-details-quick-glance-header">
          <div className="jd-details-quick-glance-col">ID</div>
          <div className="jd-details-quick-glance-col">Job Schedule</div>
          <div className="jd-details-quick-glance-col">Pay Range</div>
          <div className="jd-details-quick-glance-col">Job Family</div>
          <div className="jd-details-quick-glance-col">Shift</div>
        </div>
        <div className="jd-details-quick-glance-row">
          <div className="jd-details-quick-glance-col">{job.jobId || 'TBD'}</div>
          <div className="jd-details-quick-glance-col">{job.jobSchedule || 'TBD'}</div>
          <div className="jd-details-quick-glance-col">{job.payRange || 'TBD'}</div>
          <div className="jd-details-quick-glance-col">{job.jobFamily || 'TBD'}</div>
          <div className="jd-details-quick-glance-col">{job.shift || 'TBD'}</div>
        </div>
      </div>
      <div className="jd-about-job">
        <div className="jd-about-job-title">About the job</div>
        <div className="jd-about-job-subtitle">The role you'll contribute</div>
        <div className="jd-about-job-desc">{job.jdAbout}</div>
      </div>
      <div className="jd-team-value">
        <div className="jd-team-value-title">The value you'll bring to the team</div>
        <ul className="jd-team-value-list">
          {job.jdValues && job.jdValues.length > 0 ? (
            job.jdValues.map((val: string, idx: number) => (
              <li className="jd-team-value-item" key={idx}>{val}</li>
            ))
          ) : (
            <li className="jd-team-value-item">Details will be provided when positions become available.</li>
          )}
        </ul>
      </div>
      <div className="minimum-qualifications">
        <div className="minimum-qualifications-title">Minimum qualifications</div>
        <ul className="minimum-qualifications-list">
          {job.jdMinQual && job.jdMinQual.length > 0 ? (
            job.jdMinQual.map((val: string, idx: number) => (
              <li className="minimum-qualifications-item" key={idx}>{val}</li>
            ))
          ) : (
            <li className="minimum-qualifications-item">Requirements will be specified when positions become available.</li>
          )}
        </ul>
      </div>
      <div className="preferred-qualifications">
        <div className="preferred-qualifications-title">Preferred qualifications</div>
        <ul className="preferred-qualifications-list">
          {job.jdPrefQual && job.jdPrefQual.length > 0 ? (
            job.jdPrefQual.map((val: string, idx: number) => (
              <li className="preferred-qualifications-item" key={idx}>{val}</li>
            ))
          ) : (
            <li className="preferred-qualifications-item">Preferred qualifications will be listed when positions become available.</li>
          )}
        </ul>
      </div>
      <div className="anti-discrim-disclaimer">
        <p>This facility is an equal opportunity employer and complies with federal, state and local anti-discrimination laws, regulations and ordinances.</p>
      </div>
      <div className="jd-benefits">
        <div className="jd-benefits-title">All the benefits and perks you need for you and your family</div>
        <ul className="jd-benefits-list">
          {job.jdBenefits && job.jdBenefits.length > 0 ? (
            job.jdBenefits.map((val: string, idx: number) => (
              <li className="jd-benefits-item" key={idx}>{val}</li>
            ))
          ) : (
            <li className="jd-benefits-item">Benefits and perks will be detailed when positions become available.</li>
          )}
        </ul>
      </div>
      <div className="equal-opportunity-disclaimer">
        <p>We are an equal opportunity employer and do not tolerate discrimination based on race, color, creed, religion, national origin, sex, marital status, age or disability/handicap with respect to recruitment, selection, placement, promotion, wages, benefits and other terms and conditions of employment.</p>
      </div>
      {includeForm && (
        <form className="apply-now">
          <div className="apply-now-title">
            {hasValidJobs ? "Take the next step. Apply now!" : "Application form will be available when positions open"}
          </div>
          <fieldset className="apply-now-fieldset" disabled={!hasValidJobs}>
            <div className="apply-now-row">
              <div className="apply-now-col">
                <label htmlFor={`firstName-${job.id}`}>First Name<span className="apply-now-required">*</span></label>
                <TextField 
                  id={`firstName-${job.id}`}
                  name="firstName" 
                  variant="outlined" 
                  required 
                  fullWidth 
                  size="medium" 
                  className="apply-now-input"
                  disabled={!hasValidJobs}
                  placeholder={hasValidJobs ? "" : "No positions available"}
                />
              </div>
              <div className="apply-now-col">
                <label htmlFor={`lastName-${job.id}`}>Last Name<span className="apply-now-required">*</span></label>
                <TextField 
                  id={`lastName-${job.id}`}
                  name="lastName" 
                  variant="outlined" 
                  required 
                  fullWidth 
                  size="medium" 
                  className="apply-now-input"
                  disabled={!hasValidJobs}
                  placeholder={hasValidJobs ? "" : "No positions available"}
                />
              </div>
            </div>
            <div className="apply-now-row">
              <div className="apply-now-col">
                <label htmlFor={`email-${job.id}`}>Email<span className="apply-now-required">*</span></label>
                <TextField 
                  id={`email-${job.id}`}
                  name="email" 
                  type="email" 
                  variant="outlined" 
                  required 
                  fullWidth 
                  size="medium" 
                  className="apply-now-input"
                  disabled={!hasValidJobs}
                  placeholder={hasValidJobs ? "" : "No positions available"}
                />
              </div>
              <div className="apply-now-col">
                <label htmlFor={`phone-${job.id}`}>Phone<span className="apply-now-required">*</span></label>
                <TextField 
                  id={`phone-${job.id}`}
                  name="phone" 
                  type="tel" 
                  variant="outlined" 
                  required 
                  fullWidth 
                  size="medium" 
                  className="apply-now-input" 
                  placeholder={hasValidJobs ? "+ 1" : "No positions available"}
                  disabled={!hasValidJobs}
                />
              </div>
            </div>
            <div className="apply-now-row apply-now-row-radio">
              <FormControl required component="fieldset" style={{ width: '100%' }} disabled={!hasValidJobs}>
                <FormLabel component="legend" className="apply-now-radio-label" style={{ textAlign: 'left', width: '100%' }}>Are you a registered nurse?</FormLabel>
                <RadioGroup row name={`registeredNurse-${job.id}`} className="apply-now-radio-group">
                  <FormControlLabel value="yes" control={<Radio disabled={!hasValidJobs} />} label={<span className="apply-now-radio-text">Yes</span>} />
                  <FormControlLabel value="no" control={<Radio disabled={!hasValidJobs} />} label={<span className="apply-now-radio-text">No</span>} />
                </RadioGroup>
              </FormControl>
            </div>
          </fieldset>
          <Button 
            type="submit" 
            variant="contained" 
            className="apply-now-submit-btn"
            disabled={!hasValidJobs}
          >
            {hasValidJobs ? "Submit" : "Applications Closed"}
          </Button>
        </form>
      )}
    </>
  );

  return (
    <div className="careers-container">
      <div className="careers-hero">
        <picture>
          <source media="(max-width: 1100px)" srcSet={careerBannerMob} />
          <img src={careerBanner} alt="Careers Hero" className="careers-hero-img" />
        </picture>
        <div className="careers-hero-content">
          <div className="careers-hero-title">Discover Your Next Opportunity</div>
        </div>
      </div>
      {!hasValidJobs ? (
        <div className="careers-no-jobs-message">There are no jobs currently available</div>
      ) : (
      <div className="careers-jd-wrapper">
        {/* Desktop: Job Selection Sidebar */}
        <div className="jd-selection jd-selection-desktop">
          {hasValidJobs ? (
            jobsArray.map((job: Job, idx: number) => (
              <div
                key={job.id}
                className={`jd-selection-item${idx === selectedJobIdx ? ' selected' : ''}`}
                onClick={() => handleJobClick(idx)}
              >
                <div className="jd-selection-title">{job.title}</div>
                <div className="jd-selection-location">{job.location.state}, {job.location.country} &bull; {job.type}</div>
              </div>
            ))
          ) : (
            <div className="jd-selection-item selected">
              <div className="jd-selection-title">{emptyJob.title}</div>
              <div className="jd-selection-location">Please check back later</div>
            </div>
          )}
        </div>

        {/* Mobile: Collapsible Job List */}
        <div className="jd-selection-mobile">
          {hasValidJobs ? (
            jobsArray.map((job: Job, idx: number) => (
              <div key={job.id} className={`jd-mobile-accordion-item${expandedJobIdx === idx ? ' expanded' : ''}`}>
                <div 
                  className="jd-mobile-accordion-header"
                  onClick={() => handleMobileJobToggle(idx)}
                >
                  <div className="jd-mobile-accordion-header-content">
                    <div className="jd-selection-title">{job.title}</div>
                    <div className="jd-selection-location">{job.location.state}, {job.location.country} &bull; {job.type}</div>
                  </div>
                  <ExpandMoreIcon className={`jd-mobile-accordion-icon${expandedJobIdx === idx ? ' rotated' : ''}`} />
                </div>
                <div className="jd-mobile-accordion-content">
                  <div className="jd-details jd-details-mobile">
                    {renderJobDetails(job, true)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="jd-mobile-accordion-item expanded">
              <div className="jd-mobile-accordion-header">
                <div className="jd-mobile-accordion-header-content">
                  <div className="jd-selection-title">{emptyJob.title}</div>
                  <div className="jd-selection-location">Please check back later</div>
                </div>
              </div>
              <div className="jd-mobile-accordion-content">
                <div className="jd-details jd-details-mobile">
                  {renderJobDetails(emptyJob, true)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: Job Details */}
        <div className="jd-details jd-details-desktop">
          {renderJobDetails(selectedJob, false)}
          <form className="apply-now">
            <div className="apply-now-title">
              {hasValidJobs ? "Take the next step. Apply now!" : "Application form will be available when positions open"}
            </div>
            <fieldset className="apply-now-fieldset" disabled={!hasValidJobs}>
              <div className="apply-now-row">
                <div className="apply-now-col">
                  <label htmlFor="firstName">First Name<span className="apply-now-required">*</span></label>
                  <TextField 
                    id="firstName" 
                    name="firstName" 
                    variant="outlined" 
                    required 
                    fullWidth 
                    size="medium" 
                    className="apply-now-input"
                    disabled={!hasValidJobs}
                    placeholder={hasValidJobs ? "" : "No positions available"}
                  />
                </div>
                <div className="apply-now-col">
                  <label htmlFor="lastName">Last Name<span className="apply-now-required">*</span></label>
                  <TextField 
                    id="lastName" 
                    name="lastName" 
                    variant="outlined" 
                    required 
                    fullWidth 
                    size="medium" 
                    className="apply-now-input"
                    disabled={!hasValidJobs}
                    placeholder={hasValidJobs ? "" : "No positions available"}
                  />
                </div>
              </div>
              <div className="apply-now-row">
                <div className="apply-now-col">
                  <label htmlFor="email">Email<span className="apply-now-required">*</span></label>
                  <TextField 
                    id="email" 
                    name="email" 
                    type="email" 
                    variant="outlined" 
                    required 
                    fullWidth 
                    size="medium" 
                    className="apply-now-input"
                    disabled={!hasValidJobs}
                    placeholder={hasValidJobs ? "" : "No positions available"}
                  />
                </div>
                <div className="apply-now-col">
                  <label htmlFor="phone">Phone<span className="apply-now-required">*</span></label>
                  <TextField 
                    id="phone" 
                    name="phone" 
                    type="tel" 
                    variant="outlined" 
                    required 
                    fullWidth 
                    size="medium" 
                    className="apply-now-input" 
                    placeholder={hasValidJobs ? "+ 1" : "No positions available"}
                    disabled={!hasValidJobs}
                  />
                </div>
              </div>
              <div className="apply-now-row apply-now-row-radio">
                <FormControl required component="fieldset" style={{ width: '100%' }} disabled={!hasValidJobs}>
                  <FormLabel component="legend" className="apply-now-radio-label" style={{ textAlign: 'left', width: '100%' }}>Are you a registered nurse?</FormLabel>
                  <RadioGroup row name="registeredNurse" className="apply-now-radio-group">
                    <FormControlLabel value="yes" control={<Radio disabled={!hasValidJobs} />} label={<span className="apply-now-radio-text">Yes</span>} />
                    <FormControlLabel value="no" control={<Radio disabled={!hasValidJobs} />} label={<span className="apply-now-radio-text">No</span>} />
                  </RadioGroup>
                </FormControl>
              </div>
            </fieldset>
            <Button 
              type="submit" 
              variant="contained" 
              className="apply-now-submit-btn"
              disabled={!hasValidJobs}
            >
              {hasValidJobs ? "Submit" : "Applications Closed"}
            </Button>
          </form>
        </div>
      </div>
      )}
    </div>
  );
};

export default Careers; 